import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { CATEGORY_LABELS, CATEGORY_SINGULAR, SIZE_LABEL } from "@/lib/categories";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { X, Star } from "lucide-react";

export default function ProductPage() {
    const { slug } = useParams();
    const { add } = useCart();
    const [product, setProduct] = useState(null);
    const [activeImg, setActiveImg] = useState(0);
    const [selModel, setSelModel] = useState("");
    const [selCarats, setSelCarats] = useState("");
    const [selLength, setSelLength] = useState("");
    const [selFinition, setSelFinition] = useState("Or blanc 14kt");
    const [zoomOpen, setZoomOpen] = useState(false);
    const [review, setReview] = useState({ author: "", rating: 5, title: "", body: "", photo: "" });
    const [submittingReview, setSubmittingReview] = useState(false);

    useEffect(() => {
        api.get(`/products/${slug}`)
            .then((r) => {
                setProduct(r.data);
                const v = r.data.variants?.[0];
                if (v) {
                    setSelModel(v.model);
                    setSelCarats(v.carats);
                    setSelLength(v.length_cm);
                }
            })
            .catch(() => {});
    }, [slug]);

    const facets = useMemo(() => {
        if (!product) return { models: [], carats: [], lengths: [] };
        const models = [...new Set(product.variants.map((v) => v.model))];
        const carats = [...new Set(product.variants.filter((v) => v.model === selModel).map((v) => v.carats))].filter(Boolean);
        const lengths = [...new Set(
            product.variants.filter((v) => v.model === selModel && v.carats === selCarats).map((v) => v.length_cm)
        )];
        return { models, carats, lengths };
    }, [product, selModel, selCarats]);

    const gallery = useMemo(() => {
        if (!product) return [];
        const base = product.images || [];
        if (selFinition.includes("jaune") && product.images_jaune?.length) return [...product.images_jaune, ...base];
        if (selFinition.includes("rose") && product.images_rose?.length) return [...product.images_rose, ...base];
        return base;
    }, [product, selFinition]);

    const selectedVariant = useMemo(() => {
        if (!product) return null;
        return product.variants.find(
            (v) => v.model === selModel && v.carats === selCarats && v.length_cm === selLength
        );
    }, [product, selModel, selCarats, selLength]);

    if (!product) {
        return <div data-testid="product-loading" className="max-w-7xl mx-auto px-4 py-24 text-slate-500">Chargement…</div>;
    }

    const reviews = product.reviews || [];
    const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;
    const gi = Math.min(activeImg, Math.max(gallery.length - 1, 0));

    const pickCarats = (c) => {
        setSelCarats(c);
        const avail = product.variants.filter((v) => v.model === selModel && v.carats === c).map((v) => v.length_cm);
        if (avail.length && !avail.includes(selLength)) setSelLength(avail[0]);
    };
    const pickModel = (m) => {
        setSelModel(m);
        const first = product.variants.find((v) => v.model === m);
        if (first) { setSelCarats(first.carats); setSelLength(first.length_cm); }
    };

    const addToCart = () => {
        if (!selectedVariant) {
            toast.error("Choisissez une variante complète.");
            return;
        }
        if (selectedVariant.stock <= 0 && product.availability !== "commande") {
            toast.error("Rupture de stock sur cette variante.");
            return;
        }
        add({
            product_id: product.id,
            variant_id: selectedVariant.id,
            finition: selFinition,
            name: product.name,
            variant_label: `${selectedVariant.model} · ${selectedVariant.carats} · ${selectedVariant.length_cm} · ${selFinition}`,
            price_cad: selectedVariant.price_cad,
            image: product.images?.[0],
            slug: product.slug,
        });
        toast.success("Ajouté au panier");
    };

    const submitReview = async (e) => {
        e.preventDefault();
        setSubmittingReview(true);
        try {
            await api.post("/reviews", { product_id: product.id, ...review, photo: review.photo || null });
            toast.success("Merci ! Votre avis sera publié après modération.");
            setReview({ author: "", rating: 5, title: "", body: "", photo: "" });
        } catch {
            toast.error("Erreur — réessayez.");
        } finally {
            setSubmittingReview(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 pb-32 md:pb-16">
            <div className="text-xs text-slate-500 mb-6 tracking-wider uppercase">
                <Link to="/boutique" className="hover:text-white">Boutique</Link>
                <span className="mx-2">/</span>
                <Link to={`/boutique/${product.category}`} className="hover:text-white">
                    {CATEGORY_LABELS[product.category] || product.category}
                </Link>
                <span className="mx-2">/</span>
                <span className="text-slate-300">{product.name}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Gallery */}
                <div className="lg:col-span-7">
                    <div
                        data-testid="product-main-image"
                        className="zoom-wrap rounded-3xl overflow-hidden border border-white/10 bg-black/40 cursor-zoom-in"
                        onClick={() => setZoomOpen(true)}
                    >
                        <img
                            src={fileUrl(gallery[gi])}
                            alt={product.name}
                            className="w-full aspect-square object-cover"
                        />
                    </div>
                    <div className="mt-4 grid grid-cols-4 gap-3">
                        {gallery.map((img, i) => (
                            <button
                                key={i}
                                data-testid={`product-thumb-${i}`}
                                onClick={() => setActiveImg(i)}
                                className={`rounded-xl overflow-hidden border ${activeImg === i ? "border-white/60" : "border-white/10"}`}
                            >
                                <img src={fileUrl(img)} alt="" className="w-full aspect-square object-cover" />
                            </button>
                        ))}
                    </div>
                    <div className="mt-6 glass-card rounded-2xl p-4 flex items-center gap-3" data-testid="gra-badge">
                        <span className="text-[#D97706] text-lg">✦</span>
                        <div>
                            <div className="text-sm font-medium text-slate-100">Certificat GRA inclus</div>
                            <div className="text-xs text-slate-400 mt-0.5">Numéro de série unique, vérifiable en ligne</div>
                        </div>
                    </div>
                </div>

                {/* Info */}
                <div className="lg:col-span-5">
                    <div className="text-[10px] uppercase tracking-[0.35em] text-slate-500 mb-3">
                        {CATEGORY_SINGULAR[product.category] || product.category}
                    </div>
                    <h1 data-testid="product-title" className="font-serif-luxe text-3xl sm:text-4xl chrome-text font-semibold">
                        {product.name}
                    </h1>
                    {avg && (
                        <div data-testid="product-rating" className="mt-2 flex items-center gap-2 text-sm text-slate-400">
                            <Star className="w-4 h-4 fill-[#D97706] text-[#D97706]" /> {avg}/5 · {reviews.length} avis
                        </div>
                    )}
                    <p className="mt-4 text-slate-400 leading-relaxed">{product.description}</p>

                    <div className="mt-6 space-y-5">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Modèle</div>
                            <div className="flex flex-wrap gap-2">
                                {facets.models.map((m) => (
                                    <button
                                        key={m}
                                        data-testid={`variant-model-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        onClick={() => pickModel(m)}
                                        className={`variant-pill ${selModel === m ? "active" : ""}`}
                                    >{m}</button>
                                ))}
                            </div>
                        </div>
                        {facets.carats.length > 0 && (
                        <div>
                            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Carats</div>
                            <div className="flex flex-wrap gap-2">
                                {facets.carats.map((c) => (
                                    <button
                                        key={c}
                                        data-testid={`variant-carats-${c.replace(/[^a-z0-9]+/g, "-")}`}
                                        onClick={() => pickCarats(c)}
                                        className={`variant-pill ${selCarats === c ? "active" : ""}`}
                                    >{c}</button>
                                ))}
                            </div>
                        </div>
                        )}
                        <div>
                            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Finition</div>
                            <div className="flex flex-wrap gap-2">
                                {["Or blanc 14kt", "Or jaune 14kt", "Or rose 14kt"].map((f) => (
                                    <button
                                        key={f}
                                        data-testid={`variant-finition-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        onClick={() => { setSelFinition(f); setActiveImg(0); }}
                                        className={`variant-pill ${selFinition === f ? "active" : ""}`}
                                    >{f}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
                                {SIZE_LABEL[product.category] || "Taille"}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {facets.lengths.map((l) => (
                                    <button
                                        key={l}
                                        data-testid={`variant-length-${l.replace(/[^a-z0-9]+/g, "-")}`}
                                        onClick={() => setSelLength(l)}
                                        className={`variant-pill ${selLength === l ? "active" : ""}`}
                                    >{l}</button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 glass-card rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-500">Prix</div>
                                <div data-testid="product-price" className="text-3xl font-serif-luxe text-slate-100 mt-1">
                                    {selectedVariant ? `${selectedVariant.price_cad.toFixed(0)} $` : "—"} <span className="text-sm text-slate-400">CAD</span>
                                </div>
                            </div>
                            {selectedVariant && (
                                <div data-testid="product-availability" className="text-xs text-right">
                                    {product.availability === "commande" ? (
                                        <span className="text-[#D97706] font-medium">Sur commande<br /><span className="text-slate-400">2-3 semaines ouvrables</span></span>
                                    ) : (
                                        <span className="text-emerald-400 font-medium">En stock<br /><span className="text-slate-400">2-3 jours ouvrables</span></span>
                                    )}
                                </div>
                            )}
                        </div>
                        {selectedVariant && (
                            <div data-testid="product-installments" className="mt-3 text-xs text-slate-400 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                                ou <span className="text-slate-100 font-medium">4 versements de {(selectedVariant.price_cad / 4).toFixed(2)} $</span> sans intérêt
                                avec Klarna, ou par mensualités avec Affirm — offert au paiement
                            </div>
                        )}
                        <button
                            data-testid="add-to-cart-btn"
                            onClick={addToCart}
                            disabled={!selectedVariant || (selectedVariant.stock <= 0 && product.availability !== "commande")}
                            className="btn-primary w-full mt-4"
                        >
                            {product.availability === "commande" ? "Commander (2-3 semaines)" : "Ajouter au panier"}
                        </button>
                        <div className="mt-4 text-[11px] text-slate-500 space-y-1">
                            <div>{product.material}</div>
                            <div>Livraison Postes Canada · Gratuite dès 500 $ CAD</div>
                            <div>Retours 14 jours — produit non porté</div>
                        </div>
                    </div>

                    {/* Détails du bijou */}
                    <div data-testid="product-details" className="mt-6 glass-card rounded-2xl p-5">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Détails du bijou</div>
                        <ul className="space-y-3 text-sm text-slate-300">
                            <li className="flex gap-2"><span className="text-slate-500 w-24 shrink-0">Pierres</span> Moissanite VVS1 (éclat comparable au diamant)</li>
                            <li className="flex gap-2"><span className="text-slate-500 w-24 shrink-0">Matériau</span> Argent Sterling 925 plaqué or 14kt</li>
                            <li className="flex gap-2"><span className="text-slate-500 w-24 shrink-0">Plaquage</span> Or jaune, or blanc ou or rose 14kt — au choix</li>
                            <li className="flex gap-2" data-testid="detail-resistance"><span className="text-slate-500 w-24 shrink-0">Résistance</span> Hypoallergénique et anti-ternissement</li>
                            <li className="flex gap-2"><span className="text-slate-500 w-24 shrink-0">Certificat</span> GRA individuel, numéro de série vérifiable</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Reviews */}
            <section className="mt-20">
                <div className="hair-divider mb-12" />
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <h2 className="font-serif-luxe text-2xl sm:text-3xl text-slate-100 mb-6">Avis clients</h2>
                        {reviews.length === 0 && (
                            <div data-testid="reviews-empty" className="text-slate-500">Aucun avis pour l'instant. Soyez le premier.</div>
                        )}
                        <div className="space-y-4">
                            {reviews.map((r) => (
                                <div key={r.id} data-testid={`review-${r.id}`} className="glass-card rounded-2xl p-5">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-medium text-slate-100">{r.author}</div>
                                        <div className="flex gap-0.5">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-[#D97706] text-[#D97706]" : "text-slate-600"}`} />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="text-sm text-slate-200 mt-2 font-medium">{r.title}</div>
                                    <p className="text-sm text-slate-400 mt-1 leading-relaxed">{r.body}</p>
                                    {r.photo && (
                                        <img src={fileUrl(r.photo)} alt="Avis" className="mt-3 w-24 h-24 object-cover rounded-lg" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <h3 className="font-serif-luxe text-2xl text-slate-100 mb-6">Laisser un avis</h3>
                        <form onSubmit={submitReview} data-testid="review-form" className="glass-card rounded-2xl p-5 space-y-4">
                            <div>
                                <label>Nom</label>
                                <input data-testid="review-author-input" required value={review.author}
                                    onChange={(e) => setReview({ ...review, author: e.target.value })} />
                            </div>
                            <div>
                                <label>Note</label>
                                <select data-testid="review-rating-select" value={review.rating}
                                    onChange={(e) => setReview({ ...review, rating: parseInt(e.target.value) })}>
                                    {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}/5</option>)}
                                </select>
                            </div>
                            <div>
                                <label>Titre</label>
                                <input data-testid="review-title-input" required value={review.title}
                                    onChange={(e) => setReview({ ...review, title: e.target.value })} />
                            </div>
                            <div>
                                <label>Votre avis</label>
                                <textarea data-testid="review-body-input" required rows={4} value={review.body}
                                    onChange={(e) => setReview({ ...review, body: e.target.value })} />
                            </div>
                            <div>
                                <label>Photo (URL, optionnel)</label>
                                <input data-testid="review-photo-input" value={review.photo}
                                    onChange={(e) => setReview({ ...review, photo: e.target.value })} placeholder="https://…" />
                            </div>
                            <button data-testid="review-submit-btn" type="submit" disabled={submittingReview} className="btn-primary w-full">
                                Envoyer mon avis
                            </button>
                        </form>
                    </div>
                </div>
            </section>

            {/* Sticky mobile buy bar */}
            <div className="buy-bar md:hidden" data-testid="mobile-buy-bar">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <div className="text-xs text-slate-500">Prix</div>
                        <div className="text-xl font-serif-luxe text-slate-100">
                            {selectedVariant ? `${selectedVariant.price_cad.toFixed(0)} $` : "—"}
                        </div>
                    </div>
                    <button
                        data-testid="mobile-add-to-cart-btn"
                        onClick={addToCart}
                        disabled={!selectedVariant || (selectedVariant.stock <= 0 && product.availability !== "commande")}
                        className="btn-primary flex-1"
                    >
                        {product.availability === "commande" ? "Commander" : "Ajouter"}
                    </button>
                </div>
            </div>

            {/* Image Zoom */}
            {zoomOpen && (
                <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" data-testid="image-zoom-lightbox" onClick={() => setZoomOpen(false)}>
                    <button data-testid="image-zoom-close" className="absolute top-6 right-6" aria-label="Fermer">
                        <X className="w-8 h-8 text-slate-200" />
                    </button>
                    <img src={fileUrl(gallery[gi])} alt={product.name} className="max-w-full max-h-[90vh] object-contain" />
                </div>
            )}
        </div>
    );
}
